import { PDFDocument } from "pdf-lib";

const PAGE_SIZE_TOLERANCE = 2;
const PREVIEW_PAGE_MARGIN = 24;
const A4_PAGE_WIDTH = 595.28;
const A4_PAGE_HEIGHT = 841.89;

interface PdfPageSize {
  width: number;
  height: number;
}

const isSamePageSize = (
  baseSize: PdfPageSize,
  nextSize: PdfPageSize,
): boolean =>
  Math.abs(baseSize.width - nextSize.width) <= PAGE_SIZE_TOLERANCE &&
  Math.abs(baseSize.height - nextSize.height) <= PAGE_SIZE_TOLERANCE;

export const hasMixedPageSizes = (pdfDocument: PDFDocument): boolean => {
  const [firstPage, ...restPages] = pdfDocument.getPages();

  if (!firstPage) {
    return false;
  }

  const firstPageSize = firstPage.getSize();

  return restPages.some(
    (page) => !isSamePageSize(firstPageSize, page.getSize()),
  );
};

export const normalizePdfPageSizes = async (
  sourcePdfDocument: PDFDocument,
  title?: string,
): Promise<Uint8Array> => {
  const normalizedPdfDocument = await PDFDocument.create();

  if (title) {
    normalizedPdfDocument.setTitle(title);
  }

  for (const sourcePage of sourcePdfDocument.getPages()) {
    const { width: sourceWidth, height: sourceHeight } = sourcePage.getSize();

    if (!sourceWidth || !sourceHeight) {
      continue;
    }

    const embeddedPage = await normalizedPdfDocument.embedPage(sourcePage);
    const normalizedPage = normalizedPdfDocument.addPage([
      A4_PAGE_WIDTH,
      A4_PAGE_HEIGHT,
    ]);
    const availableWidth = A4_PAGE_WIDTH - PREVIEW_PAGE_MARGIN * 2;
    const availableHeight = A4_PAGE_HEIGHT - PREVIEW_PAGE_MARGIN * 2;
    const scale = Math.min(
      availableWidth / sourceWidth,
      availableHeight / sourceHeight,
    );
    const scaledWidth = sourceWidth * scale;
    const scaledHeight = sourceHeight * scale;

    normalizedPage.drawPage(embeddedPage, {
      x: (A4_PAGE_WIDTH - scaledWidth) / 2,
      y: (A4_PAGE_HEIGHT - scaledHeight) / 2,
      width: scaledWidth,
      height: scaledHeight,
    });
  }

  return normalizedPdfDocument.save();
};

export const normalizePdfBlobIfNeeded = async (
  pdfBlob: Blob,
  title?: string,
): Promise<Blob> => {
  const originalPdfBytes = await pdfBlob.arrayBuffer();
  const pdfDocument = await PDFDocument.load(originalPdfBytes, {
    ignoreEncryption: true,
  });

  if (!hasMixedPageSizes(pdfDocument)) {
    if (!title) {
      return pdfBlob;
    }

    pdfDocument.setTitle(title);

    return new Blob([await pdfDocument.save()], {
      type: "application/pdf",
    });
  }

  return new Blob([await normalizePdfPageSizes(pdfDocument, title)], {
    type: "application/pdf",
  });
};
